import { useQueryClient } from '@tanstack/react-query'

import { getRenderTemplateApiV1TemplatesTemplateIdRenderGetQueryOptions } from '#/shared/api/generated/communication/theCargoCommunication'

import { useLoadTemplate } from './use-load-template'

export interface RenderedTemplateContent {
  /** Template body with `{{ … }}` placeholders resolved against the shipment. */
  body: string
  /** Rendered subject (null for SMS templates), undefined when unavailable. */
  subject: string | undefined
}

/**
 * Resolve a template against a shipment via
 * `GET /api/v1/templates/{template_id}/render?shipment_id=…` and return the
 * expanded body and subject. Falls back to the raw template when the render
 * endpoint fails or yields an empty body, so the editor is never left blank.
 */
export function useRenderTemplate() {
  const queryClient = useQueryClient()
  const loadTemplate = useLoadTemplate()
  return async (
    templateId: string,
    shipmentId: string,
  ): Promise<RenderedTemplateContent> => {
    try {
      const response = await queryClient.fetchQuery(
        getRenderTemplateApiV1TemplatesTemplateIdRenderGetQueryOptions(
          templateId,
          { shipment_id: shipmentId },
        ),
      )
      const { body, subject } = response.data
      if (body.length > 0) return { body, subject: subject ?? undefined }
    } catch {
      // fall through to the raw, unrendered template below
    }
    const raw = await loadTemplate(templateId)
    return { body: raw.body, subject: raw.subject ?? undefined }
  }
}
